import { z } from 'zod'
import type { AITool } from './types'
import type { DateRange } from '../../types'
// import { getLocalDayKey } from '../timezone-utils' // Currently unused
import { DataProcessor } from '../../services/dataProcessor'
import { isWorkoutInRange } from '../calendar-utils'
import { format } from 'date-fns'

const dateRangeSchema = z.object({
  preset: z.enum(['last28days', '28to56days', '56to84days'])
    .describe('Time range for performance metrics: last28days (current period), 28to56days (previous period), or 56to84days (2 periods ago)')
})

export const getUserPerformanceMetrics: AITool = {
  name: 'getUserPerformanceMetrics',
  description: 'Get training metrics and statistics.',
  parameters: dateRangeSchema,
  
  execute: async (args, context) => {
    const { workoutData, userSettings } = context
    
    if (!workoutData.workouts?.length) {
      return {
        summary: 'No workout data available.',
        periodMetrics: null,
        weeklyStats: null,
        calendarData: []
      }
    }

    // Process all workouts to ensure consistency with widgets
    const processedWorkouts = DataProcessor.ensureWorkoutsProcessed(workoutData.workouts)
    
    // Determine date range based on preset (28-day periods to match calendar widget)
    const now = new Date()
    let dateRange: DateRange
    let periodLabel: string
    let isCurrentPeriod = false
    
    switch (args.preset) {
      case '28to56days':
        // Previous period: 28-56 days ago
        dateRange = {
          start: new Date(now.getTime() - 55 * 24 * 60 * 60 * 1000),
          end: new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000)
        }
        periodLabel = 'Days 28-56'
        break
      case '56to84days':
        // 2 periods ago: 56-84 days ago
        dateRange = {
          start: new Date(now.getTime() - 83 * 24 * 60 * 60 * 1000),
          end: new Date(now.getTime() - 56 * 24 * 60 * 60 * 1000)
        }
        periodLabel = 'Days 56-84'
        break
      case 'last28days':
      default:
        // Current period: last 28 days including today (exactly 4 weeks)
        dateRange = {
          start: new Date(now.getTime() - 27 * 24 * 60 * 60 * 1000),
          end: now
        }
        periodLabel = 'Last 4 Weeks'
        isCurrentPeriod = true
    }

    // Process calendar data using DataProcessor (same as widgets)
    const allCalendarData = DataProcessor.processWorkoutsToCalendar(processedWorkouts)
    
    // Filter calendar data for the specific period (same logic as calendar widget)
    const periodCalendarData = allCalendarData.filter(day => {
      const dayDate = typeof day.date === 'string' ? new Date(day.date) : day.date
      return isWorkoutInRange(dayDate, dateRange)
    })

    // Get user target workouts per week (same logic as calendar widget)
    let targetPerWeek: number
    if (userSettings?.settings?.algorithms?.targetWorkoutsPerWeek === 'auto') {
      // Auto-detect from weekly split if available
      const weeklySplit = userSettings?.weeklySplit
      if (weeklySplit) {
        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
        const activeDays = days.filter(
          (day) => {
            const dayRoutines = weeklySplit[day as keyof typeof weeklySplit]
            return Array.isArray(dayRoutines) && dayRoutines.length > 0
          }
        ).length
        targetPerWeek = Math.max(1, Math.min(7, activeDays))
      } else {
        targetPerWeek = 3 // Default if no weekly split configured
      }
    } else {
      targetPerWeek = userSettings?.settings?.algorithms?.targetWorkoutsPerWeek || 3
    }

    // Calculate period metrics (replicate calendar widget logic exactly)
    const sessionCount = periodCalendarData.length
    const totalSets = periodCalendarData.reduce((sum, day) => sum + (day.sets || 0), 0)
    const daysInPeriod = 28 // Always 28 days
    const targetSessionsForPeriod = Math.round((targetPerWeek / 7) * daysInPeriod)
    const consistency = targetSessionsForPeriod > 0 
      ? Math.min(100, Math.round((sessionCount / targetSessionsForPeriod) * 100)) 
      : 0
    const activeDays = sessionCount
    const restDays = Math.max(0, daysInPeriod - activeDays)
    const averageSessionsPerWeek = Math.round((sessionCount / 4) * 10) / 10 // 4 weeks

    // Extract period routines with counts and colors (same as calendar widget)
    const routineMap = new Map<string, { count: number, color: string }>()
    periodCalendarData.forEach(day => {
      day.routineNames?.forEach((name: string, index: number) => {
        const color = day.routineColors?.[index] || DataProcessor.getWorkoutColor(name)
        if (routineMap.has(name)) {
          routineMap.get(name)!.count++
        } else {
          routineMap.set(name, { count: 1, color })
        }
      })
    })
    
    const periodRoutines = Array.from(routineMap.entries())
      .map(([name, { count, color }]) => ({ name, count, color }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4) // Top 4 routines

    // Calculate weekly stats using DataProcessor (same as weekly stats widget)
    const _weeklyStats = DataProcessor.processWorkoutsToStats(processedWorkouts)

    // Current period only metrics
    let currentPeriodOnly = undefined
    if (isCurrentPeriod) {
      const sevenDaysAgo = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000)
      const last7DaysData = allCalendarData.filter(day => {
        const dayDate = typeof day.date === 'string' ? new Date(day.date) : day.date
        return dayDate >= sevenDaysAgo && dayDate <= now
      })
      
      currentPeriodOnly = {
        sessionsLast7Days: last7DaysData.length
      }
    }

    // Format period dates for display
    const startStr = format(dateRange.start, 'MMM d')
    const endStr = format(dateRange.end, 'MMM d')
    const periodDateRange = dateRange.start.getMonth() === dateRange.end.getMonth() 
      ? `${startStr} - ${format(dateRange.end, 'd')}`
      : `${startStr} - ${endStr}`

    // Calculate muscle group distribution for the selected period
    const muscleGroupSets: Record<string, number> = {}
    
    // Count sets by muscle group from the filtered period data
    processedWorkouts
      .filter(workout => {
        const workoutDate = new Date(workout.start_time)
        return workoutDate >= dateRange.start && workoutDate <= dateRange.end
      })
      .forEach(workout => {
        workout.exercises?.forEach(exercise => {
          // Get muscle contributions (primary movers only for now)
          const contributions = exercise.primaryMover ? [{ muscleGroup: exercise.primaryMover, contribution: 1 }] : []
          
          // Count sets for each muscle group
          const validSets = exercise.sets?.filter(set => set.type !== 'empty') || []
          
          contributions.forEach(({ muscleGroup }) => {
            muscleGroupSets[muscleGroup] = (muscleGroupSets[muscleGroup] || 0) + validSets.length
          })
        })
      })
    
    const totalMuscleGroupSets = Object.values(muscleGroupSets).reduce((sum: number, sets: number) => sum + sets, 0)
    const muscleGroupDistribution: Record<string, number> = {}
    
    if (totalMuscleGroupSets > 0) {
      Object.entries(muscleGroupSets).forEach(([muscle, sets]) => {
        muscleGroupDistribution[muscle] = Math.round((sets / totalMuscleGroupSets) * 100)
      })
    }

    // Calculate volume change compared to previous period
    let volumeChangePercent = 0
    if (isCurrentPeriod) {
      // Compare current period (last 28 days) to previous period (28-56 days ago)
      const previousStart = new Date(now.getTime() - 55 * 24 * 60 * 60 * 1000)
      const previousEnd = new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000)
      
      const previousPeriodData = allCalendarData.filter(day => {
        const dayDate = typeof day.date === 'string' ? new Date(day.date) : day.date
        return dayDate >= previousStart && dayDate <= previousEnd
      })
      
      const previousPeriodSets = previousPeriodData.reduce((sum, day) => sum + (day.sets || 0), 0)
      
      if (previousPeriodSets > 0) {
        volumeChangePercent = Math.round(((totalSets - previousPeriodSets) / previousPeriodSets) * 100)
      }
    }

    return {
      summary: `Performance for ${periodLabel} (${periodDateRange}): ${consistency}% consistency (${sessionCount} of ${targetSessionsForPeriod} target sessions), averaging ${averageSessionsPerWeek} sessions per week.`,
      sessionsCompleted: sessionCount,
      sessionsTarget: targetSessionsForPeriod,
      consistencyRate: consistency,
      totalSets: totalSets,
      volumeChangePercent: volumeChangePercent,
      averageSessionLength: totalSets > 0 && sessionCount > 0 ? Math.round((totalSets / sessionCount) * 2.5) : 0, // Estimate ~2.5 min per set
      muscleGroupDistribution: muscleGroupDistribution,
      daysActive: activeDays,
      daysRest: restDays,
      topRoutines: periodRoutines.map(r => r.name).slice(0, 3),
      sessionsLast7Days: currentPeriodOnly?.sessionsLast7Days || 0,
      period: periodLabel,
      dateRange: periodDateRange
    }
  }
}